import { t } from "../i18n/index.ts";
import { el } from "./dom.ts";
import {
  CLOSE_ICON_SVG,
  LIGHTBOX_NEXT_ICON,
  LIGHTBOX_PREV_ICON,
  mapControlButton,
} from "../map-embed-controls.ts";

export interface SpotPhotoLightboxOpts {
  urls: string[];
  captions?: Array<string | null | undefined>;
  startIndex?: number;
}

/** Full-size spot gallery viewer (editor preview + public page). */
export function openSpotPhotoLightbox(opts: SpotPhotoLightboxOpts): () => void {
  const urls = opts.urls.filter(Boolean);
  if (!urls.length) return () => {};
  const captions = opts.captions ?? [];
  let index = Math.min(Math.max(opts.startIndex ?? 0, 0), urls.length - 1);

  const overlay = el("div", "spot-photo-lightbox");
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", t("rt.spot.lightbox.title"));

  let closed = false;
  const closeLightbox = () => {
    if (closed) return;
    closed = true;
    window.removeEventListener("keydown", onKey, true);
    overlay.remove();
  };

  const backdrop = el("button", "spot-photo-lightbox-backdrop");
  backdrop.type = "button";
  backdrop.setAttribute("aria-label", t("rt.spot.lightbox.close"));

  const panel = el("div", "spot-photo-lightbox-panel");
  const close = mapControlButton("spot-photo-lightbox-close", t("rt.spot.lightbox.close"), CLOSE_ICON_SVG, () =>
    closeLightbox(),
  );

  const img = el("img", "spot-photo-lightbox-img") as HTMLImageElement;
  img.alt = "";
  img.decoding = "async";

  const captionEl = el("p", "spot-photo-lightbox-caption");

  const prev = mapControlButton("spot-photo-lightbox-nav spot-photo-lightbox-prev", t("rt.spot.lightbox.prev"), LIGHTBOX_PREV_ICON, () =>
    step(-1),
  );
  const next = mapControlButton("spot-photo-lightbox-nav spot-photo-lightbox-next", t("rt.spot.lightbox.next"), LIGHTBOX_NEXT_ICON, () =>
    step(1),
  );

  const paint = () => {
    img.src = urls[index]!;
    const cap = captions[index]?.trim();
    captionEl.textContent = cap ?? "";
    captionEl.hidden = !cap;
    img.alt = cap ?? "";
    const multi = urls.length > 1;
    prev.hidden = !multi;
    next.hidden = !multi;
    prev.disabled = index <= 0;
    next.disabled = index >= urls.length - 1;
  };

  const step = (dir: -1 | 1) => {
    index = Math.min(urls.length - 1, Math.max(0, index + dir));
    paint();
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeLightbox();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      step(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      step(+1);
    }
  };

  backdrop.addEventListener("click", () => closeLightbox());
  panel.addEventListener("click", (e) => e.stopPropagation());
  overlay.append(backdrop, panel);
  const stage = el("div", "spot-photo-lightbox-stage");
  stage.append(prev, img, next);
  panel.append(close, stage, captionEl);
  document.body.append(overlay);
  paint();
  window.addEventListener("keydown", onKey, true);
  close.focus();
  return closeLightbox;
}

/** Wire spot card thumbnail + gallery images to the lightbox. */
export function bindSpotCardPhotoLightbox(
  card: HTMLElement,
  galleryUrls: string[],
  galleryCaptions: Array<string | null | undefined>,
  thumbUrl?: string | null,
): void {
  const gallery = galleryUrls.filter(Boolean);
  const list = gallery.length ? gallery : thumbUrl?.trim() ? [thumbUrl.trim()] : [];
  const caps = gallery.length ? galleryCaptions : [];
  if (!list.length) return;
  const openAt = (startIndex: number) => {
    openSpotPhotoLightbox({ urls: list, captions: caps, startIndex });
  };
  const thumb = card.querySelector<HTMLElement>(".qp-spot-card-thumb:not(.qp-spot-card-thumb-ph)");
  if (thumb) {
    thumb.classList.add("is-lightbox-trigger");
    thumb.tabIndex = 0;
    thumb.setAttribute("role", "button");
    const go = () => openAt(0);
    thumb.addEventListener("click", (e) => {
      e.stopPropagation();
      go();
    });
    thumb.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        go();
      }
    });
  }
  card.querySelectorAll<HTMLElement>(".qp-spot-images .qp-spot-image:not(.is-missing)").forEach((imgEl, i) => {
    imgEl.classList.add("is-lightbox-trigger");
    imgEl.tabIndex = 0;
    imgEl.setAttribute("role", "button");
    const go = () => openAt(i);
    imgEl.addEventListener("click", (e) => {
      e.stopPropagation();
      go();
    });
    imgEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        go();
      }
    });
  });
}
